import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";
import { ApiKeysManager } from "../components/api/ApiKeysManager";
import { ApiDocumentation } from "../components/api/ApiDocumentation";

export default function ApiAccessPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("python");
  const isBusinessPlan = user?.subscription === "business";

  const loadKeys = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/apikeys/list`);
      setKeys(data);
    } catch (err) {
      console.error("Load API keys failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleGenerate = async () => {
    setError("");
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/apikeys/generate`);
      setNewKey(data.key);
      loadKeys();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (prefix) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/apikeys/${prefix}`);
      loadKeys();
    } catch (err) {
      console.error("Revoke API key failed:", err);
    }
  };

  if (!isBusinessPlan) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">API Access</h1>
            <p className="text-gray-600">Programmatic access to QAIRO AI</p>
          </div>
          <div className="max-w-lg mx-auto text-center py-12 bg-white border border-gray-200 rounded-xl" data-testid="api-upgrade-prompt">
            <div className="w-16 h-16 bg-[#0066FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl font-black text-[#0066FF]">&#123;&#125;</span>
            </div>
            <h2 className="text-xl font-bold mb-2">Business Plan Required</h2>
            <p className="text-gray-600 mb-6">API access is available exclusively on the Business plan ($149/mo).</p>
            <a href="/pricing" className="inline-block bg-[#0066FF] text-white px-6 py-3 font-bold hover:bg-[#0052CC] transition-colors rounded-lg" data-testid="upgrade-to-business">
              Upgrade to Business
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">API Access</h1>
          <p className="text-gray-600">Integrate QAIRO AI into your applications</p>
        </div>

        <ApiKeysManager
          keys={keys}
          loading={loading}
          newKey={newKey}
          error={error}
          generating={generating}
          onGenerate={handleGenerate}
          onRevoke={handleRevoke}
        />

        <ApiDocumentation activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </DashboardLayout>
  );
}
